import { useCallback, useMemo, useState } from 'react';

import { Toggle } from '@components';
import { useDeepEffect } from '@hooks';
import type { ToggleProps } from '@components';

const defaultCanSelect = () => true;

export const useTableSelection = <T extends { id: string }>(params: {
  nodes: T[];
  canSelect?: (item: T) => boolean;
}) => {
  const { nodes, canSelect = defaultCanSelect } = params;
  const [selected, setSelected] = useState<string[]>([]);

  useDeepEffect(() => {
    setSelected([]);
  }, [nodes]);

  const selectableIds = useMemo(() => {
    return nodes.filter(n => canSelect(n)).map(item => item.id);
  }, [nodes, canSelect]);
  const { selectedSet, selectedNodes } = useMemo(() => {
    const selectedSet = new Set(selected);
    const selectedNodes = nodes.filter(node => selectedSet.has(node.id));
    return { selectedSet, selectedNodes };
  }, [nodes, selected]);

  const isSelected = useCallback(
    (id: string) => selectedSet.has(id),
    [selectedSet]
  );
  const toggleSelect = useCallback((id: string) => {
    setSelected(old => {
      return old.includes(id) ? old.filter(item => item !== id) : [...old, id];
    });
  }, []);

  const selectAllElement = (
    <Toggle
      variant="checkbox"
      value={!!selected.length}
      onChange={() => setSelected(selected.length ? [] : selectableIds)}
    />
  );

  const renderSelectElement = (node: T, props?: ToggleProps) => {
    return (
      !!canSelect(node) && (
        <Toggle
          {...props}
          variant="checkbox"
          value={isSelected(node.id)}
          onChange={() => toggleSelect(node.id)}
        />
      )
    );
  };

  return {
    selected,
    selectedNodes,
    selectableIds,
    selectAllElement,

    isSelected,
    setSelected,
    toggleSelect,
    renderSelectElement,
  };
};
